import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * Marché live — vue « Sur une période » : mouvement de nuit (veille → ouverture)
 * contre mouvement de séance (ouverture → clôture), calculé sur le bulletin officiel.
 *
 * ⚠️ Statistique descriptive, réservée à l'administration. La relation négative
 * entre les deux mouvements est en partie mécanique (le cours d'ouverture entre
 * dans les deux calculs) : ne jamais la présenter comme une attente pour la
 * séance en cours.
 */

export interface ApresEcart {
    nb: number;
    ecart_moyen_pct: number | null;
    seance_moyenne_pct: number | null;
}

export interface NuitSeanceStats {
    nb_lignes: number;
    nb_lignes_ecartees: number;
    part_ouverture_inchangee_pct: number | null;
    nuit_moyenne_pct: number | null;
    seance_moyenne_pct: number | null;
    /** Pente de la séance sur l'écart d'ouverture ; null si l'échantillon est trop court. */
    pente: number | null;
    correlation: number | null;
    apres_ouverture_en_hausse: ApresEcart;
    apres_ouverture_en_baisse: ApresEcart;
}

export interface NuitSeanceValeur extends NuitSeanceStats {
    societe_symbole: string;
    nom_societe: string | null;
}

export interface NuitSeanceMeta {
    debut: string;
    fin: string;
    nb_seances: number;
    premiere_seance: string | null;
    derniere_seance: string | null;
    nb_valeurs_relation_mesurable: number;
    nb_valeurs_pente_negative: number;
    min_lignes_relation: number;
    seuil_ecart_fort_pct: number;
    source: string;
    avertissement: string;
}

/** `mois` = profondeur de la période, comptée depuis la dernière séance du bulletin ; null = tout l'historique. */
export function useLiveMarketPeriode(mois: number | null) {
    const { token } = useAuth();
    const [marche, setMarche] = useState<NuitSeanceStats | null>(null);
    const [valeurs, setValeurs] = useState<NuitSeanceValeur[]>([]);
    const [meta, setMeta] = useState<NuitSeanceMeta | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const charger = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const entetes = { Authorization: `Bearer ${token}` };
            const base = `${API_URL}/v1/admin/live-market/nuit-seance`;
            // La fin de période est la dernière séance du bulletin : le serveur la
            // connaît, le navigateur non. On ne lui passe donc qu'une profondeur.
            const url = mois === null ? `${base}?debut=2015-01-01` : `${base}?mois=${mois}`;
            const reponse = await fetch(url, { headers: entetes });
            if (!reponse.ok) throw new Error(String(reponse.status));
            const corps = await reponse.json();
            setMarche(corps.data?.marche ?? null);
            setValeurs(corps.data?.valeurs ?? []);
            setMeta(corps.meta?.debut ? corps.meta : null);
        } catch (err) {
            console.error('Erreur marché live (période) :', err);
            setError(
                err instanceof Error && err.message === '403'
                    ? 'Accès refusé : permission admin_platform requise.'
                    : 'Impossible de charger l’analyse sur la période. Réessayez dans un instant.'
            );
        } finally {
            setLoading(false);
        }
    }, [token, mois]);

    useEffect(() => {
        charger();
    }, [charger]);

    return { marche, valeurs, meta, loading, error };
}
